import { ApplicationController } from "../../support/application-controller";

export default class extends ApplicationController {
    static targets = [
        "triggerButton",
        "floatingBar",
        "selectedCount",
        "selectAll",
        "syncButton",
        "tableCheckboxHeader"
    ];

    connect() {
        this.syncUrl = this.data.get("syncUrl");
        this.selectionMode = false;
        this.selectedIds = new Set();
    }

    enterSelectionMode() {
        this.selectionMode = true;
        this.selectedIds.clear();

        // Hide the trigger button
        if (this.hasTriggerButtonTarget) {
            this.triggerButtonTarget.classList.add("hidden");
        }

        // Show floating bar
        if (this.hasFloatingBarTarget) {
            this.floatingBarTarget.classList.remove("hidden");
        }

        // Show table checkbox header
        if (this.hasTableCheckboxHeaderTarget) {
            this.tableCheckboxHeaderTarget.classList.remove("hidden");
        }

        // Add checkboxes to eligible items
        this._getSubscriptionItems().forEach(item => {
            const hasMapping = item.dataset.hasHubspotMapping === "true";
            const subId = item.dataset.subscriptionId;

            if (hasMapping) {
                this._addCheckbox(item, subId);
            } else {
                item.classList.add("hubspot-sync-ineligible");
            }
        });

        this._updateCount();
    }

    exitSelectionMode() {
        this.selectionMode = false;
        this.selectedIds.clear();

        // Show trigger button
        if (this.hasTriggerButtonTarget) {
            this.triggerButtonTarget.classList.remove("hidden");
        }

        // Hide floating bar
        if (this.hasFloatingBarTarget) {
            this.floatingBarTarget.classList.add("hidden");
        }

        // Hide table checkbox header
        if (this.hasTableCheckboxHeaderTarget) {
            this.tableCheckboxHeaderTarget.classList.add("hidden");
        }

        // Remove checkboxes and ineligible styling
        this.element.querySelectorAll(".hubspot-sync-checkbox-wrapper").forEach(el => el.remove());
        this.element.querySelectorAll(".hubspot-sync-checkbox-cell").forEach(el => el.remove());
        this._getSubscriptionItems().forEach(item => {
            item.classList.remove("hubspot-sync-ineligible");
            item.classList.remove("hubspot-sync-selected");
        });

        // Reset select all
        if (this.hasSelectAllTarget) {
            this.selectAllTarget.checked = false;
        }
    }

    toggleSelection(event) {
        const checkbox = event.target;
        const subId = checkbox.dataset.subscriptionId;
        const item = this._findItemBySubId(subId);

        if (checkbox.checked) {
            this.selectedIds.add(parseInt(subId));
            if (item) item.classList.add("hubspot-sync-selected");
        } else {
            this.selectedIds.delete(parseInt(subId));
            if (item) item.classList.remove("hubspot-sync-selected");
        }

        this._updateCount();
        this._updateSelectAllState();
    }

    toggleSelectAll() {
        const checked = this.selectAllTarget.checked;

        this.element.querySelectorAll('.hubspot-sync-checkbox').forEach(cb => {
            cb.checked = checked;
            const subId = cb.dataset.subscriptionId;
            const item = this._findItemBySubId(subId);

            if (checked) {
                this.selectedIds.add(parseInt(subId));
                if (item) item.classList.add("hubspot-sync-selected");
            } else {
                this.selectedIds.delete(parseInt(subId));
                if (item) item.classList.remove("hubspot-sync-selected");
            }
        });

        this._updateCount();
    }

    sync() {
        if (this.selectedIds.size === 0) return;

        var count = this.selectedIds.size;
        if (!confirm("Sync " + count + " subscription(s) and their related entities to HubSpot?")) return;

        // Disable sync button during request
        if (this.hasSyncButtonTarget) {
            this.syncButtonTarget.disabled = true;
            this.syncButtonTarget.textContent = "Syncing...";
        }

        var self = this;

        fetch(this.syncUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "X-CSRF-Token": this._getCSRFToken()
            },
            body: JSON.stringify({ subscription_ids: Array.from(this.selectedIds) })
        })
        .then(function(response) {
            return response.json().then(function(data) {
                return { ok: response.ok, data: data };
            });
        })
        .then(function(result) {
            if (result.ok && result.data.success) {
                self._showToast("Sync enqueued for " + result.data.count + " subscription(s). Check HubSpot Tools for progress.", "success");
                self.exitSelectionMode();
            } else {
                self._showToast(result.data.error || "Failed to enqueue sync", "error");
            }
        })
        .catch(function(error) {
            self._showToast("Network error: " + error.message, "error");
        })
        .then(function() {
            if (self.hasSyncButtonTarget) {
                self.syncButtonTarget.disabled = false;
                self.syncButtonTarget.textContent = "Sync Now";
            }
        });
    }

    // Private helpers

    _getSubscriptionItems() {
        return this.element.querySelectorAll(
            ".subscriptions-list__subscription-item, .subscriptions-table__row"
        );
    }

    _findItemBySubId(subId) {
        return this.element.querySelector(`[data-subscription-id="${subId}"]`);
    }

    _addCheckbox(item, subId) {
        // Don't add duplicate checkboxes
        if (item.querySelector('.hubspot-sync-checkbox-wrapper')) return;

        const wrapper = document.createElement("label");
        wrapper.className = "hubspot-sync-checkbox-wrapper";

        const checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.className = "hubspot-sync-checkbox";
        checkbox.dataset.subscriptionId = subId;
        checkbox.dataset.action = "change->admin--subscription-hubspot-sync#toggleSelection";

        wrapper.appendChild(checkbox);

        // For table rows, prepend a td; for cards, prepend to the card
        if (item.tagName === "TR") {
            const td = document.createElement("td");
            td.className = "hubspot-sync-checkbox-cell";
            td.appendChild(wrapper);
            item.prepend(td);
        } else {
            item.prepend(wrapper);
        }
    }

    _updateCount() {
        if (this.hasSelectedCountTarget) {
            this.selectedCountTarget.textContent = this.selectedIds.size;
        }
        if (this.hasSyncButtonTarget) {
            this.syncButtonTarget.disabled = this.selectedIds.size === 0;
        }
    }

    _updateSelectAllState() {
        if (!this.hasSelectAllTarget) return;
        const allCheckboxes = this.element.querySelectorAll('.hubspot-sync-checkbox');
        const allChecked = allCheckboxes.length > 0 && Array.from(allCheckboxes).every(cb => cb.checked);
        this.selectAllTarget.checked = allChecked;
    }

    _getCSRFToken() {
        const meta = document.querySelector('meta[name="csrf-token"]');
        return meta ? meta.content : null;
    }

    _showToast(message, type) {
        const toast = document.createElement("div");
        toast.className = `hubspot-sync-toast hubspot-sync-toast--${type}`;
        toast.textContent = message;
        document.body.appendChild(toast);

        requestAnimationFrame(() => toast.classList.add("hubspot-sync-toast--visible"));

        setTimeout(() => {
            toast.classList.remove("hubspot-sync-toast--visible");
            setTimeout(() => toast.remove(), 300);
        }, 5000);
    }
}
